import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import UserAvatar from "../UserAvatar";
import { getNicheMeta } from "../niche";
import { useAuth } from "../../context/AuthContext";
import { checkFollowing, toggleFollow } from "../../services/followService";
import { formatCount } from "../../pages/creator/creatorHelpers";
import NominateCreatorButton from "../credits/NominateCreatorButton";
import {
  creatorProfilePath,
  splitDisplayName,
} from "../../utils/featuredDisplay";

function nicheStyle(key) {
  const styles = {
    wellness: { bg: "rgba(92,139,110,0.2)", color: "#7AB89A", border: "rgba(92,139,110,0.3)" },
    beauty: { bg: "rgba(139,92,139,0.2)", color: "#C49AC4", border: "rgba(139,92,139,0.3)" },
    food: { bg: "rgba(196,121,10,0.15)", color: "#C4790A", border: "rgba(196,121,10,0.2)" },
    fitness: { bg: "rgba(92,124,139,0.2)", color: "#7AA8C4", border: "rgba(92,124,139,0.3)" },
    auto: { bg: "rgba(139,110,92,0.2)", color: "#C4A07A", border: "rgba(139,110,92,0.3)" },
    mind: { bg: "rgba(110,92,139,0.2)", color: "#A47AC4", border: "rgba(110,92,139,0.3)" },
    arts: { bg: "rgba(110,92,139,0.2)", color: "#A47AC4", border: "rgba(110,92,139,0.3)" },
    independent: { bg: "rgba(92,139,110,0.2)", color: "#7AB89A", border: "rgba(92,139,110,0.3)" },
  };
  return styles[key] || styles.independent;
}

function HeroBadge({ mode, nominationCount }) {
  if (mode === "discovery") {
    return (
      <div className="ft-hero-card-badge ft-hero-card-badge--discovery">
        <span className="ft-hero-card-badge-dot ft-hero-card-badge-dot--green" />
        <span className="ft-hero-card-badge-text ft-hero-card-badge-text--green">
          Emerging Voice
        </span>
      </div>
    );
  }

  return (
    <div className="ft-hero-card-badge">
      <span className="ft-hero-card-badge-dot" />
      <span className="ft-hero-card-badge-text">
        Most Nominated
        {nominationCount > 0 ? ` · ${nominationCount} nominations` : ""}
      </span>
    </div>
  );
}

export function FeaturedHeroCard({ creator, mode }) {
  const { currentUser } = useAuth();
  const to = creatorProfilePath(creator);
  const { main, em } = splitDisplayName(creator.displayName);
  const meta = getNicheMeta(creator.niche);
  const niche = nicheStyle(meta.key);
  const handle = creator.handle || creator.username;
  const location = creator.city || creator.location || "";
  const subs = creator.followerCount || creator.supporterCount || 0;
  const creatorId = creator.uid || creator.id;
  const dropCount =
    creator.totalPosts || creator.totalDrops || creator.openDropCount || 0;
  const instagramCount = creator.social?.instagramFollowers || 0;
  const isOwner = currentUser?.uid === creatorId;

  const [following, setFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);

  useEffect(() => {
    if (!currentUser || !creatorId || isOwner) return;
    checkFollowing({ followerId: currentUser.uid, creatorId }).then(setFollowing);
  }, [currentUser, creatorId, isOwner]);

  const handleFollow = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!currentUser || !creatorId || followLoading || isOwner) return;
    setFollowLoading(true);
    try {
      const next = await toggleFollow({
        followerId: currentUser.uid,
        creatorId,
        isFollowing: following,
        creatorUsername: handle,
        creatorDisplayName: creator.displayName,
        creatorAvatar: creator.avatar || creator.avatarUrl,
      });
      setFollowing(next);
    } catch (err) {
      console.error("Featured hero follow error:", err);
    } finally {
      setFollowLoading(false);
    }
  };

  if (!to) return null;

  return (
    <article className="ft-hero-card">
      <div className="ft-hero-card-image">
        {creator.cardImage ? (
          <img src={creator.cardImage} alt="" loading="lazy" />
        ) : (
          <div className="ft-hero-card-image-fallback" />
        )}
      </div>
      <div className="ft-hero-card-gradient" />
      <HeroBadge mode={mode} nominationCount={creator.nominationCount} />
      <div className="ft-hero-card-num">{String(creator.rank).padStart(2, "0")}</div>
      <div className="ft-hero-card-content">
        <span
          className="ft-hero-card-niche"
          style={{
            background: niche.bg,
            color: niche.color,
            borderColor: niche.border,
          }}
        >
          {meta.label}
        </span>
        <div className="ft-hero-card-name">
          {main}
          {em ? (
            <>
              {" "}
              <em>{em}</em>
            </>
          ) : null}
        </div>
        <div className="ft-hero-card-handle">
          @{handle}
          {location ? ` · ${location}` : ""}
          {creator.isFoundingContributor ||
          creator.isFoundingMember ||
          creator.foundingCreator
            ? " · Founding Creator"
            : ""}
        </div>
        {creator.bio && <p className="ft-hero-card-bio">{creator.bio}</p>}
        <div className="ft-hero-card-foot">
          <div className="ft-hero-card-stats">
            <div className="ft-hero-card-stat">
              <strong>{formatCount(subs)}</strong>
              <span>Subscribers</span>
            </div>
            <div className="ft-hero-card-stat">
              <strong>{dropCount}</strong>
              <span>Drops</span>
            </div>
            <div className="ft-hero-card-stat">
              <strong>{formatCount(instagramCount)}</strong>
              <span>Instagram</span>
            </div>
            {creator.openDropCount > 0 && (
              <div className="ft-hero-card-stat">
                <strong>{creator.openDropCount} open</strong>
                <span>Right now</span>
              </div>
            )}
          </div>
          <div className="ft-hero-card-btns">
            {!isOwner && (
              <button
                type="button"
                className={`ft-hero-card-btn-follow${following ? " ft-hero-card-btn-follow--active" : ""}`}
                onClick={handleFollow}
                disabled={!currentUser || followLoading}
              >
                {following ? "Following" : "Follow"}
              </button>
            )}
            {mode === "community" && !isOwner && (
              <NominateCreatorButton
                creatorId={creatorId}
                creatorName={creator.displayName || handle}
                variant="hero"
                className="ft-hero-card-btn-nominate"
              />
            )}
            <Link to={to} className="ft-hero-card-btn-drops">
              See their drops →
            </Link>
          </div>
        </div>
      </div>
    </article>
  );
}

export function FeaturedGridCard({ creator, mode }) {
  const { currentUser } = useAuth();
  const to = creatorProfilePath(creator);
  const { main, em } = splitDisplayName(creator.displayName);
  const meta = getNicheMeta(creator.niche);
  const niche = nicheStyle(meta.key);
  const handle = creator.handle || creator.username;
  const creatorId = creator.uid || creator.id;
  const isOwner = currentUser?.uid === creatorId;

  if (!to) return null;

  return (
    <article className="ft-grid-card-wrap">
      <Link to={to} className="ft-grid-card">
      <div className="ft-grid-card-image">
        {creator.cardImage ? (
          <img src={creator.cardImage} alt="" loading="lazy" />
        ) : (
          <div className="ft-grid-card-image-fallback" />
        )}
      </div>
      <div className="ft-grid-card-gradient" />
      <div className="ft-grid-card-num">{String(creator.rank).padStart(2, "0")}</div>
      <div className="ft-grid-card-content">
        {mode === "discovery" ? (
          <span className="ft-grid-mini-badge ft-grid-mini-badge--discovery">🌱 Emerging Voice</span>
        ) : (
          <span className="ft-grid-mini-badge ft-grid-mini-badge--community">✦ Community&apos;s Choice</span>
        )}
        <span
          className="ft-grid-card-niche"
          style={{
            background: niche.bg,
            color: niche.color,
            borderColor: niche.border,
          }}
        >
          {meta.label}
        </span>
        <div className="ft-grid-card-name">
          {main}
          {em ? (
            <>
              {" "}
              <em>{em}</em>
            </>
          ) : null}
        </div>
        <div className="ft-grid-card-handle">@{handle}</div>
        <div className="ft-grid-card-foot">
          <div className="ft-grid-card-stats">
            <span className="ft-grid-card-stat">
              <strong>{formatCount(creator.followerCount || creator.supporterCount || 0)}</strong> subs
            </span>
            <span className="ft-grid-card-stat">
              <strong>{creator.openDropCount || 0}</strong> open
            </span>
            {mode === "community" && creator.nominationCount > 0 && (
              <span className="ft-grid-card-stat ft-grid-card-stat--gold">
                <strong>{creator.nominationCount}</strong> noms
              </span>
            )}
          </div>
          {creator.viewsLeft > 0 && (
            <span className="ft-grid-card-open">
              <span className="ft-grid-card-open-dot" />
              {creator.viewsLeft} left
            </span>
          )}
        </div>
      </div>
      </Link>
      {mode === "community" && !isOwner && (
        <NominateCreatorButton
          creatorId={creatorId}
          creatorName={creator.displayName || handle}
          variant="compact"
          className="ft-grid-card-nominate"
        />
      )}
    </article>
  );
}

export function CategoryAvatarPreview({ creators, extraLabel }) {
  const visible = creators.slice(0, 4);
  const overflow = Math.max(creators.length - 4, 0);

  return (
    <div className="ft-cat-avatars">
      {visible.map((c) => (
        <UserAvatar
          key={c.id}
          src={c.avatarUrl || c.avatar}
          alt={c.displayName}
          name={c.displayName}
          className="ft-cat-av"
          imgClassName="ft-cat-av-img"
          initialsClassName="ft-cat-av-initials"
        />
      ))}
      {overflow > 0 && <span className="ft-cat-av-count">+{overflow}</span>}
      {extraLabel && <span className="ft-cat-av-label">{extraLabel}</span>}
    </div>
  );
}
